import { httpClient, HttpClientResponse } from '@work4all/data';

import { Environment, environment } from '../../../environments';

interface AutoDiscoverServerParams {
  domain: string | undefined;
  environment: string;
}

export type AutoDiscoverV2Response = {
  apiUrls: string[];
  assignedFrontendUrl: string;
  assignedFrontendVersion: string;
};

const STORAGE_KEY_PREFIX = 'w4a_autodiscovery_cache';

const getStorageKey = (domain: string | undefined): string => {
  const domainKey = domain || 'default';
  return `${STORAGE_KEY_PREFIX}_${domainKey}`;
};

const storeLastUrl = (domain: string | undefined, url: string): void => {
  try {
    const key = getStorageKey(domain);
    localStorage.setItem(key, url);
  } catch (e) {
    console.warn('Failed to store autodiscovery URL in localStorage:', e);
  }
};

const getLastUrl = (domain: string | undefined): string | null => {
  try {
    const key = getStorageKey(domain);
    return localStorage.getItem(key);
  } catch (e) {
    console.warn('Failed to retrieve autodiscovery URL from localStorage:', e);
    return null;
  }
};

export const getAutodiscoveryBaseUrl = async (
  email: string
): Promise<HttpClientResponse<AutoDiscoverV2Response> | null> => {
  let domain;
  const atPos = email.indexOf('@');
  if (atPos !== -1) {
    domain = email.substring(atPos + 1, email.length);
  }

  const autoDiscoveryApiEnvironmentParam =
    AutoDiscoveryApiEnvironmentParamMap[environment.configuration];

  try {
    const res = await httpClient.get<
      AutoDiscoverV2Response,
      AutoDiscoverServerParams
    >({
      url: 'https://ws.work4all.de/api/service/autodiscoverserver2',
      params: {
        domain: domain || undefined,
        environment: autoDiscoveryApiEnvironmentParam,
      },
      contentType: 'application/json',
    });

    // Store successful response in localStorage for future fallback
    if (res.data && res.data.apiUrls.length > 0 && res.status === 200) {
      storeLastUrl(domain, res.data.apiUrls[0]);
    }

    // Return the full V2 response - React Navigate will handle navigation
    return { data: res.data, status: res.status, headers: res.headers };
  } catch (e) {
    // Try to fallback to last known URL for this domain
    const lastUrl = getLastUrl(domain);
    if (lastUrl) {
      // Return a response structure with V2 format for fallback
      return {
        data: {
          apiUrls: [lastUrl],
          assignedFrontendUrl: '',
          assignedFrontendVersion: '',
        },
        status: 200,
        headers: new Headers(),
      };
    }

    // No fallback available, return the error
    return e;
  }
};

const AutoDiscoveryApiEnvironmentParamMap: Record<
  Environment['configuration'],
  string
> = {
  development: 'Dev',
  preview: 'Dev',
  staging: 'Staging',
  production: 'Production',
};
